import { ContentHeader } from "../components/ContentHeader";
import { useApi } from "../hooks/useApi";
import { OPERATIONS } from "../nav";
import type { BuildsResponse, RecentResponse, AwaitingApprovalResponse } from "../api/types";

export function CicdTab() {
  const meta = OPERATIONS.find((e) => e.id === "cicd")!;
  const builds   = useApi<BuildsResponse>("/command-center/cicd/builds");
  const recent   = useApi<RecentResponse>("/command-center/cicd/recent");
  const awaiting = useApi<AwaitingApprovalResponse>("/command-center/cicd/awaiting-approval");

  const loading = builds.loading || recent.loading || awaiting.loading;
  const anyError = builds.error || recent.error || awaiting.error;

  if (loading) {
    return (
      <>
        <ContentHeader title={meta.label} description={meta.desc} />
        <div className="loading"><div className="loading-spinner" /><p>Loading CI/CD state…</p></div>
      </>
    );
  }
  if (anyError || !builds.data) {
    return (
      <>
        <ContentHeader title={meta.label} description={meta.desc} />
        <div className="empty">
          command-center-api not reachable. Run <code>./serve.sh</code> from <code>WaffyMonitoring/</code>.
        </div>
      </>
    );
  }

  const b = builds.data;
  const r = recent.data;
  const a = awaiting.data;
  const fails = r?.recent_failures?.length ?? 0;

  return (
    <>
      <ContentHeader title={meta.label} description={meta.desc} />
      <div className="cov-summary">
        <Pill cls={(b.running?.length ?? 0) > 0 ? "warn" : "ok"} n={b.running?.length ?? 0}>running</Pill>
        <Pill cls={(b.queue?.length ?? 0) > 0 ? "warn" : "ok"} n={b.queue?.length ?? 0}>queued</Pill>
        <Pill cls={(a?.awaiting?.length ?? 0) > 0 ? "warn" : "ok"} n={a?.awaiting?.length ?? 0}>awaiting approval</Pill>
        <Pill cls={fails > 5 ? "crit" : fails > 0 ? "warn" : "ok"} n={fails}>recent failures</Pill>
      </div>

      {b.status !== "ok" ? (
        <div className="empty" style={{ textAlign: "start" }}>
          <strong>command-center-api couldn't reach Jenkins.</strong> {b.reason}<br />
          Set <code>JENKINS_USER</code> + <code>JENKINS_TOKEN</code> in the API environment to populate this view.
        </div>
      ) : (
        <>
          <Group title="Running now" count={b.running.length}>
            {b.running.length === 0
              ? <div className="empty" style={{ padding: 18 }}>No builds running.</div>
              : (
                <div className="grid">
                  {b.running.map((rb) => {
                    const elapsed = `${Math.floor(rb.elapsed_s / 60)}m ${rb.elapsed_s % 60}s`;
                    return (
                      <a key={`${rb.job}-${rb.number}`} className="card"
                         href={rb.url} target="_blank" rel="noopener noreferrer"
                         style={{ display: "flex", flexDirection: "column", gap: 8, textDecoration: "none" }}>
                        <div className="card-head">
                          <div className="card-name">{rb.job}</div>
                          <span className="kind-badge">#{rb.number}</span>
                        </div>
                        <div className="card-meta">
                          <span style={{ color: "var(--text-muted)", fontSize: 11.5 }}>running {elapsed}</span>
                        </div>
                      </a>
                    );
                  })}
                </div>
              )}
          </Group>

          {(a?.awaiting?.length ?? 0) > 0 && (
            <Group title="Awaiting approval" count={a!.awaiting.length}>
              <div className="grid">
                {a!.awaiting.map((aw) => (
                  <a key={`${aw.job}-${aw.number}`} className="card tier-P0"
                     href={aw.approve_url} target="_blank" rel="noopener noreferrer"
                     style={{ display: "flex", flexDirection: "column", gap: 8, textDecoration: "none" }}>
                    <div className="card-head">
                      <div className="card-name">{aw.job}</div>
                      <span className="kind-badge">#{aw.number}</span>
                    </div>
                    <div className="card-meta">
                      <span style={{ color: "var(--text-muted)", fontSize: 11.5 }}>{aw.messages?.[0] ?? "awaiting input"}</span>
                    </div>
                  </a>
                ))}
              </div>
            </Group>
          )}

          {(b.queue?.length ?? 0) > 0 && (
            <Group title="Queued" count={b.queue.length}>
              <ul className="list">
                {b.queue.map((q) => (
                  <li key={q.id}>
                    <a href={q.url} target="_blank" rel="noopener">{q.job}</a>
                    {" — "}<span style={{ color: "var(--text-muted)", fontSize: 12 }}>{q.why}</span>
                  </li>
                ))}
              </ul>
            </Group>
          )}

          {(r?.by_pipeline_success_rate?.length ?? 0) > 0 && (
            <Group title="Per-pipeline success (24h)">
              <table className="cov-matrix">
                <thead><tr><th>Pipeline</th><th>Total</th><th>Success</th><th>Rate</th></tr></thead>
                <tbody>
                  {r!.by_pipeline_success_rate.map((p) => {
                    const rate = p.success_rate ?? 0;
                    const cls = rate >= 0.9 ? "yes" : rate >= 0.5 ? "unknown" : "no";
                    return (
                      <tr key={p.job}>
                        <td><code style={{ fontFamily: "var(--font-mono)" }}>{p.job}</code></td>
                        <td>{p.total}</td>
                        <td>{p.success}</td>
                        <td className={`cov-cell ${cls}`}>
                          {p.success_rate != null ? Math.round(p.success_rate * 100) + "%" : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Group>
          )}

          {fails > 0 && (
            <Group title="Recent failures (24h)" count={fails}>
              <ul className="list">
                {r!.recent_failures.map((f, i) => {
                  const when = new Date(f.timestamp).toLocaleString("en-US", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" });
                  return (
                    <li key={`${f.job}-${f.number}-${i}`}>
                      <a href={f.url} target="_blank" rel="noopener">{f.job} #{f.number}</a>
                      {" · "}<span className="kind-tag alert">{f.result ?? "FAILURE"}</span>
                      {" · "}<span style={{ color: "var(--text-muted)", fontSize: 11.5 }}>{when}</span>
                    </li>
                  );
                })}
              </ul>
            </Group>
          )}
        </>
      )}
    </>
  );
}

function Pill({ cls, n, children }: { cls: "ok" | "warn" | "crit"; n: number; children: React.ReactNode }) {
  return <span className={`cov-pill ${cls}`}><span className="dot" /><strong>{n}</strong> {children}</span>;
}
function Group({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) {
  return (
    <>
      <div className="section-header">
        <h2>{title}</h2>
        {count != null && <span className="count">{count}</span>}
      </div>
      {children}
    </>
  );
}
